import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/button'
import { Counter } from 'ui/counter'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from 'ui/drawer'
import { Label } from 'ui/label'
import { toast } from 'ui/sonner'

import { getSnoozeUntil } from '~/boundedContext/cards/utils/card'
import { ScopeSnoozeToFrontLanguageControl } from '~/boundedContext/settings/components/scope-snooze-to-front-language-control'

import { formatSnoozeUntil, snoozeDaysSchema } from './utils'

type SnoozeCardDrawerProps = {
  onConfirm: (snoozeCount: number) => void
  onOpenChange: (open: boolean) => void
  open: boolean
}

export const SnoozeCardDrawer = ({ onConfirm, onOpenChange, open }: SnoozeCardDrawerProps) => {
  const { i18n, t } = useTranslation()
  const [snoozeCount, setSnoozeCount] = useState(1)

  const setSnoozeDays = (nextValue: unknown) => {
    const parsedValue = snoozeDaysSchema.safeParse(nextValue)
    if (!parsedValue.success) {
      toast.error(t('Invalid snooze delay days'))
      return
    }

    setSnoozeCount(parsedValue.data)
  }

  const handleConfirm = () => {
    const isValidDays = snoozeDaysSchema.safeParse(snoozeCount).success
    if (!isValidDays) return toast.error(t('Invalid snooze delay days'))

    onConfirm(snoozeCount)
    onOpenChange(false)
  }

  return (
    <Drawer onOpenChange={onOpenChange} open={open}>
      <DrawerContent>
        <div className="mx-auto w-full max-w-sm">
          <DrawerHeader>
            <DrawerTitle>{t('Snooze card')}</DrawerTitle>
            <DrawerDescription>
              {t('Snoozed until {{date}}', {
                date: formatSnoozeUntil(getSnoozeUntil(snoozeCount), i18n.language),
              })}
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-4 px-4 pb-2">
            <div>
              <Label className="mb-2 block">{t('Snooze count')}</Label>
              <Counter
                decreaseLabel={t('Decrease snooze delay')}
                increaseLabel={t('Increase snooze delay')}
                inputLabel={t('Snooze count')}
                onValueChange={setSnoozeDays}
                value={snoozeCount}
              />
            </div>
            <ScopeSnoozeToFrontLanguageControl />
          </div>
          <DrawerFooter className="p-4">
            <Button onClick={handleConfirm}>{t('Snooze card')}</Button>
            <DrawerClose render={<Button variant="outline" />}>{t('Cancel')}</DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
